import { onScopeDispose } from 'vue'
import { storeToRefs } from 'pinia'
import { useAiraWidget } from '../store/useAiraWidgetStore'
import type { AiraPropertyValue } from '../types'

/**
 * Boolean data binding properties expected on Aira's view model.
 *
 * State machine Inputs are deprecated in the Rive editor, so the contextual states
 * are driven through a ViewModel instead. The shipped artboard does not expose these
 * properties yet — until the designers export the view model described in
 * docs/AIRA_RIVE_SPEC.md these calls are silent no-ops. Names must match that spec
 * character for character.
 */
export const AIRA_PROPERTY = {
  angry: 'angry',
  tracking: 'tracking',
  wantsToSay: 'wantsToSay',
} as const

export type AiraPropertyName = (typeof AIRA_PROPERTY)[keyof typeof AIRA_PROPERTY]

interface UseAiraOptions {
  /** Restore the default widget state when the calling component unmounts. */
  resetOnDispose?: boolean
}

/**
 * Page-level API for the contextual Aira widget.
 *
 * ```ts
 * const aira = useAira()
 * aira.say('Эти джинсы садятся маломерно')
 * aira.setAngry(true)
 * ```
 */
export function useAira(options: UseAiraOptions = {}) {
  const { resetOnDispose = true } = options
  const store = useAiraWidget()
  const { isVisible, isReady, hint, hasHint, propertyNames } = storeToRefs(store)

  const write = (path: string, value?: AiraPropertyValue) => store.write(path, value)
  const trigger = (path: string) => store.trigger(path)

  const wantsToSay = (value: boolean) => write(AIRA_PROPERTY.wantsToSay, value)
  const setTracking = (value: boolean) => write(AIRA_PROPERTY.tracking, value)
  const setAngry = (value: boolean) => write(AIRA_PROPERTY.angry, value)

  /** Show a bubble and put Aira into her "has something to say" state. */
  const say = (text: string | null) => {
    store.say(text)
    wantsToSay(Boolean(text))
  }

  if (resetOnDispose) {
    onScopeDispose(() => {
      store.reset()
      wantsToSay(false)
    })
  }

  return {
    isVisible,
    isReady,
    hint,
    hasHint,
    propertyNames,

    show: store.show,
    hide: store.hide,
    say,
    clearHint: () => say(null),
    write,
    trigger,
    wantsToSay,
    setTracking,
    setAngry,
  }
}
